import { CountryFilterPair, CountryMasterPgsFilterPair, Filters, Option } from '../pageConfig/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import {
  convertYTDOrMATtoPeriodType,
  getDefaultValueForDerivablePeriodicity,
  getPeriodValueByTimeFrame,
} from '../PeriodUtils';

/**
 * Retrieves the list of PGs based on subscription
 *
 * @param timeFrame selected timeFrame
 * @param pgsByCountryBySubscription PGs based on countries and subscription
 * @param countries selected countries
 * @param productGroups seleceted PGs
 * @returns List of product groups
 */
export const getPgsBySubscription = (
  timeFrame: Periodicity,
  pgsByCountryBySubscription: CountryFilterPair[],
  periodLabel: string,
  countries?: Option[],
  productGroups?: Option[],
): string[] => {
  if (!countries || !timeFrame || countries.length === 0 || pgsByCountryBySubscription.length === 0) {
    return [];
  }
  const countryCodes = countries.map((c) => c.value);

  let pgsByCountry = pgsByCountryBySubscription
    .filter(
      (pg) =>
        pg.periodType &&
        countryCodes.includes(pg.country) &&
        convertYTDOrMATtoPeriodType(getDefaultValueForDerivablePeriodicity(timeFrame), periodLabel).toUpperCase() ===
          pg.periodType.toUpperCase(),
    )
    .flatMap((pg) => pg.filter.options);

  let pgsBySubscription;

  if (!productGroups || productGroups.length === 0) {
    pgsBySubscription = pgsByCountry.map((pg) => pg.value);
  } else {
    const pgs = productGroups.map((pg) => pg.value);
    pgsBySubscription = pgsByCountry.filter((pg) => pgs.includes(pg.value)).map((o) => o.value);
  }

  return pgsBySubscription;
};

const getAvailableMasterPgsByTimeFrame = (
  timeFrame: Periodicity,
  periodValue: string,
  availableMasterPgsByCountry: CountryMasterPgsFilterPair[],
) => {
  switch (timeFrame) {
    case Periodicity.Monthly:
    case Periodicity.RollingQuarter:
    case Periodicity.CalendarQuarter:
    case Periodicity.CustomMonths:
      return availableMasterPgsByCountry.flatMap((pg) => pg.timePeriod?.monthly);
    case Periodicity.CustomWeeks:
    case Periodicity.Weekly:
      return availableMasterPgsByCountry.flatMap((pg) => pg.timePeriod?.weekly);
    case Periodicity.YTD:
    case Periodicity.MAT:
      if (periodValue.includes('Week')) {
        return availableMasterPgsByCountry.flatMap((pg) => pg.timePeriod?.weekly);
      } else {
        return availableMasterPgsByCountry.flatMap((pg) => pg.timePeriod?.monthly);
      }
    default:
      return [];
  }
};

/**
 * Retrieves the list of unavailable PGs for the period.
 * It filters the PGs based on subscription, selected countries and selected product groups
 *
 * @param periodValue period value
 * @param timeFrame Selected timeframe (Weekly/Monthly)
 * @param pgsByCountryBySubscription List of pgs based on subscription and country
 * @param masterPgsByCountry List of pgs based on country
 * @param countries List of selected countries
 * @param productGroups List of selected PGs
 * @returns list of unavailable PG for the selected period
 */
export const getUnavailablePGsByPeriod = (
  periodValue: string,
  timeFrame: Periodicity,
  pgsByCountryBySubscription: CountryFilterPair[],
  masterPgsByCountry: CountryMasterPgsFilterPair[],
  countries?: Option[],
  productGroups?: Option[],
): string[] => {
  if (!countries) {
    return [];
  }
  const countryCodes = countries.map((country) => country.value);
  const pgsBySubscription = getPgsBySubscription(
    timeFrame,
    pgsByCountryBySubscription,
    periodValue,
    countries,
    productGroups,
  );

  return getUnavailableProductGroups(
    pgsBySubscription,
    periodValue,
    timeFrame,
    masterPgsByCountry,
    countryCodes,
    productGroups,
  );
};

export const getUnavailableProductGroups = (
  subscribedProductGroups: string[],
  periodValue: string,
  timeFrame: Periodicity,
  masterPgsByCountry: CountryMasterPgsFilterPair[],
  countries: string[],
  productGroups?: Option[],
) => {
  const availableMasterPgsByCountry = masterPgsByCountry.filter((pg) => countries.includes(pg.country));
  const availableMasterPgsByTimeFrame = getAvailableMasterPgsByTimeFrame(
    timeFrame,
    periodValue,
    availableMasterPgsByCountry,
  );

  const periodValueToCheck = getPeriodValueByTimeFrame(timeFrame, periodValue);
  switch (timeFrame) {
    case Periodicity.Monthly:
    case Periodicity.RollingQuarter:
    case Periodicity.Weekly:
    case Periodicity.YTD:
    case Periodicity.MAT:
      break;
    case Periodicity.CalendarQuarter:
      if (
        !availableMasterPgsByTimeFrame.flatMap((timeFrameLabel) => timeFrameLabel?.label).includes(periodValueToCheck)
      ) {
        return [];
      }
      break;
    case Periodicity.CustomMonths:
    case Periodicity.CustomWeeks:
      if (
        availableMasterPgsByTimeFrame
          .filter((availablePgByTF) => availablePgByTF?.value.includes(periodValueToCheck))
          .flatMap((pg) => pg?.availablePgs).length < 1
      ) {
        return [];
      }
      break;
    default:
      //additional handling will be added when we implement for custom months
      return [];
  }

  const availableMasterPgsByPeriod = availableMasterPgsByTimeFrame
    .filter((availablePgByTF) => availablePgByTF?.value === periodValueToCheck)
    .flatMap((pg) => pg?.availablePgs);

  let availableMasterPgs: any[] = [];
  if (!productGroups || productGroups.length === 0) {
    availableMasterPgs = availableMasterPgsByPeriod.map((pg) => pg?.value);
  } else {
    const pgs = productGroups.map((pg) => pg.value);
    availableMasterPgs = availableMasterPgsByPeriod
      .filter((pg) => {
        return !!pg && pgs.includes(pg.value);
      })
      .map((pg) => {
        return pg?.value;
      });
  }

  // in multi country case, the checking need to check the count of the available pg is equal to the subscribed pg
  // as the existing include checking will return all pg is available whenever there are 1 country have the pg for the period.
  if (countries.length > 1) {
    return subscribedProductGroups.length - availableMasterPgs.length > 0 ? subscribedProductGroups : [];
  }

  return subscribedProductGroups.filter((pg) => {
    return !availableMasterPgs.includes(pg);
  });
};

export const getIsDisabledStatus = (productSpecificationFilters: Filters[]): boolean => {
  return productSpecificationFilters?.some((filter) => filter.disabled && filter.dataType === 'BRAND');
};
